(() => {
  // Shopify Forms owns its fields, consent, validation, and submission. This
  // adapter supplies presentation, policy links, SMS disclosure, and the buying-guide message.
  const GUIDE_STORAGE_KEY = 'serenova-buying-guide';
  const initialized = new WeakSet();

  const formatGuideMessage = (answers) => Array.isArray(answers)
    ? `Buying guide selections:\n${answers.map((answer) => {
      const values = Array.isArray(answer.values) && answer.values.length ? answer.values.join(', ') : 'No preference selected';
      return `${answer.question}: ${values}`;
    }).join('\n')}`
    : '';

  const readGuideMessage = () => {
    if (new URLSearchParams(window.location.search).get('from') !== 'buying-guide') return '';
    try {
      const guide = JSON.parse(sessionStorage.getItem(GUIDE_STORAGE_KEY));
      return formatGuideMessage(guide?.answers);
    } catch {
      return '';
    }
  };

  const initialize = (panel) => {
    if (initialized.has(panel)) return;
    initialized.add(panel);
    const ownerKey = panel.dataset.guideOwner?.trim();
    const consumerGuide = ownerKey
      ? Array.from(document.querySelectorAll('[data-buying-guide][data-guide-consumer="true"]'))
        .find((guide) => guide.dataset.guideSectionKey === ownerKey)
      : panel.closest('[data-buying-guide][data-guide-consumer="true"]');
    const isGuideContact = panel.hasAttribute('data-guide-contact-stage') && Boolean(consumerGuide);
    const isConsumerContact = panel.classList.contains('contact-app-panel--full-width') || isGuideContact;
    let guideMessage = isGuideContact ? '' : readGuideMessage();
    const privacyPolicyUrl = panel.dataset.privacyPolicyUrl;
    const roots = new WeakSet();
    const updates = new Set();
    let attempts = 0;
    let connectionTimer;
    let contactActive = false;
    let contactCompleted = false;

    const enhance = (embed) => {
      const root = embed.shadowRoot;
      if (!root || roots.has(root)) return;
      roots.add(root);
      if (isConsumerContact) {
        embed.dataset.serenovaConsumerContact = '';
      }
      const stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = panel.dataset.formStyles;
      stylesheet.dataset.serenovaFormStyles = '';
      root.append(stylesheet);
      let hydrated = false;
      let hydratedMessage;
      let previousGuideMessage = '';
      let submitted = false;
      let previouslySuccessful = Boolean(root.querySelector('[data-current-step="success"]'));

      // Listen inside the provider's shadow root: submit is not composed across it.
      // A submit attempt alone never unlocks the shopping recommendations.
      if (isGuideContact) root.addEventListener('submit', (event) => {
        if (!contactActive || contactCompleted || panel.hidden || previouslySuccessful
          || !(event.target instanceof HTMLFormElement)
          || event.target !== root.querySelector('form[data-testid="form"]')) return;
        submitted = true;
      }, true);

      const alignConsentOrder = () => {
        if (!isConsumerContact) return;
        const form = root.querySelector('form[data-testid="form"]');
        const submit = form?.querySelector(':scope > button[type="submit"]');
        if (!submit) return;
        // CSS places consent below Message. Keep native focus/reading order in
        // sync by moving the existing controls, with any provider error output.
        let anchor = submit;
        for (const name of ['phoneConsent', 'emailConsent']) {
          const container = Array.from(form.children).find((child) => child.querySelector(`fieldset[name="${name}"]`));
          if (!container) continue;
          const group = [container];
          let sibling = container.nextElementSibling;
          while (sibling?.matches('output[role="alert"]')) {
            group.push(sibling);
            sibling = sibling.nextElementSibling;
          }
          if (group[group.length - 1].nextElementSibling !== anchor) {
            const focused = root.activeElement;
            group.forEach((node) => form.insertBefore(node, anchor));
            if (focused?.isConnected && root.activeElement !== focused) {
              focused.focus({ preventScroll: true });
            }
          }
          anchor = container;
        }
      };

      const enhanceConsentDisclaimer = () => {
        if (!isConsumerContact || !privacyPolicyUrl) return;
        const disclaimer = root.querySelector('[class*="_formDisclaimer_"] p');
        if (!disclaimer) return;
        const policyLink = (href, label) => {
          const link = document.createElement('a');
          link.href = href;
          link.textContent = label;
          return link;
        };
        // Enhance only the provider's default plain text; preserve merchant-authored links/copy.
        if (!disclaimer.querySelector('a') &&
            disclaimer.textContent.trim() === 'View our privacy policy and terms of service.') {
          disclaimer.replaceChildren('View our ', policyLink(privacyPolicyUrl, 'privacy policy'),
            ' and ', policyLink('/policies/terms-of-service', 'terms of service'), '.');
        }

        // Phone entry remains separate from the provider-owned SMS checkbox and consent.
        if (!root.querySelector('fieldset[name="phoneConsent"]') ||
            root.querySelector('[data-serenova-sms-disclosure]')) return;
        const smsDisclosure = document.createElement('p');
        smsDisclosure.dataset.serenovaSmsDisclosure = '';
        smsDisclosure.append(
          'If you opt in to texts, you agree to receive optional recurring automated marketing texts from Serenova Spas. ',
          'Message frequency varies. Message and data rates may apply. Consent is not a condition of purchase. ',
          'To stop texts, use the unsubscribe link in a message. For help, contact office@serenovaspas.com or 212-713-7727. ',
          'Read our ', policyLink(privacyPolicyUrl, 'privacy policy'), ' and ',
          policyLink('/policies/terms-of-service', 'terms of service'), '.');
        disclaimer.after(smsDisclosure);
      };

      const update = () => {
        if (!panel.isConnected) {
          observer.disconnect();
          updates.delete(update);
          return;
        }
        enhanceConsentDisclaimer();
        alignConsentOrder();
        if (isGuideContact) {
          const submit = root.querySelector('form[data-testid="form"] > button[type="submit"]');
          const label = submit && Array.from(submit.childNodes).find((node) =>
            node.nodeType === Node.TEXT_NODE && node.textContent.trim() === 'Contact Serenova');
          if (label) label.textContent = 'Submit';
        }
        // The provider currently omits the textarea id referenced by its label.
        // Restore that association without replacing any provider-owned nodes.
        const message = root.querySelector('textarea[name="custom#message"]');
        if (message && !message.id) message.id = 'custom#message';
        if (guideMessage && message && (isGuideContact
          ? message !== hydratedMessage || guideMessage !== previousGuideMessage
          : !hydrated)) {
          hydrated = true;
          if (isGuideContact || !message.value.includes('Buying guide selections:')) {
            // Replace only the block this adapter generated, retaining the shopper's note.
            const note = isGuideContact && previousGuideMessage
              ? message.value.replace(previousGuideMessage, '').trim()
              : message.value.trim();
            const value = note ? `${guideMessage}\n\n${note}` : guideMessage;
            Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value').set.call(message, value);
            message.dispatchEvent(new Event('input', { bubbles: true }));
            message.dispatchEvent(new Event('change', { bubbles: true }));
          }
          hydratedMessage = message;
          previousGuideMessage = guideMessage;
          const notice = panel.querySelector('[data-guide-notice]');
          if (notice) notice.hidden = false;
        }
        const successful = Boolean(root.querySelector('[data-current-step="success"]'));
        if (isGuideContact) {
          const loading = panel.querySelector('[data-guide-contact-loading]');
          if (loading && contactActive) loading.hidden = contactCompleted || Boolean(message && hydrated);
          if (successful && !previouslySuccessful) {
            const freshSubmission = submitted;
            submitted = false;
            if (freshSubmission && !contactCompleted) {
              contactCompleted = true;
              const notice = panel.querySelector('[data-guide-notice]');
              if (notice) notice.hidden = true;
              if (loading) loading.hidden = true;
              panel.dispatchEvent(new CustomEvent('guide:contact-submitted'));
            }
          }
          previouslySuccessful = successful;
        } else if (hydrated && successful) {
          try { sessionStorage.removeItem(GUIDE_STORAGE_KEY); } catch { /* Storage can be unavailable. */ }
          const notice = panel.querySelector('[data-guide-notice]');
          if (notice) notice.hidden = true;
        }
      };
      const observer = new MutationObserver(update);
      observer.observe(root, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['data-current-step'] });
      updates.add(update);
      update();
    };

    // App blocks hydrate asynchronously; a bounded retry also covers a shadow
    // root being attached after the custom element is inserted into the page.
    const connect = () => {
      connectionTimer = undefined;
      if (!panel.isConnected) return;
      const embeds = Array.from(panel.querySelectorAll('shopify-forms-embed'));
      embeds.forEach(enhance);
      if (++attempts < 120 && (!embeds.length || embeds.some((embed) => !embed.shadowRoot))) {
        connectionTimer = setTimeout(connect, 250);
      }
    };
    if (isGuideContact) panel.addEventListener('guide:contact', (event) => {
      if (event.target !== panel || contactCompleted) return;
      guideMessage = formatGuideMessage(event.detail?.answers);
      contactActive = true;
      const loading = panel.querySelector('[data-guide-contact-loading]');
      if (loading) loading.hidden = false;
      if (connectionTimer !== undefined) clearTimeout(connectionTimer);
      attempts = 0;
      connect();
      updates.forEach((update) => update());
    });
    connect();
  };

  const initializeAll = () => document.querySelectorAll('[data-contact-app-panel]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll);
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();
